import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PhotoCarouselProps {
  images: string[];
}

const PhotoCarousel = ({ images }: PhotoCarouselProps) => {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);

  const goTo = useCallback((idx: number) => {
    setDirection(idx > current ? 1 : -1);
    setCurrent(idx);
  }, [current]);

  const next = useCallback(() => {
    if (current < images.length - 1) {
      setDirection(1);
      setCurrent(c => c + 1);
    }
  }, [current, images.length]);

  const prev = useCallback(() => {
    if (current > 0) {
      setDirection(-1);
      setCurrent(c => c - 1);
    }
  }, [current]);

  // Auto-advance
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent(c => {
        const next = (c + 1) % images.length;
        setDirection(1);
        return next;
      });
    }, 4000);
    return () => clearInterval(timer);
  }, [images.length]);

  return (
    <div className="relative">
      <div className="relative overflow-hidden rounded-xl aspect-square max-w-[390px] mx-auto">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.img
            key={current}
            src={images[current]}
            alt={`Slide ${current + 1}`}
            custom={direction}
            initial={{ opacity: 0, x: direction * 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction * -100 }}
            transition={{ duration: 0.3 }}
            className="w-full h-full object-cover absolute inset-0"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </AnimatePresence>
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-1.5 mt-3">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Ga naar foto ${i + 1}`}
            aria-current={i === current}
            className={`w-2 h-2 rounded-full transition-colors ${
              i === current ? 'bg-[#0F1C2E]' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default PhotoCarousel;
